import * as ExcelJS from 'exceljs';
// @ts-ignore
import { saveAs } from 'file-saver';
import { ExcelCellStyle, ExcelColors } from './types';

/**
 * Base Excel Service - Common utility functions for Excel operations
 */
export class BaseExcelService {
  /**
   * Apply styling to a cell
   */
  static applyCellStyle(cell: ExcelJS.Cell, style: ExcelCellStyle): void {
    if (style.font) {
      cell.font = style.font;
    }
    if (style.fill) {
      cell.fill = style.fill;
    }
    if (style.alignment) {
      cell.alignment = style.alignment;
    }
    if (style.border) {
      cell.border = style.border;
    }
  }

  /**
   * Create a header cell with predefined styling
   */
  static createHeaderCell(
    worksheet: ExcelJS.Worksheet,
    cellAddress: string,
    value: string,
    size: number = 16,
    bgColor: string = ExcelColors.PRIMARY_HEADER
  ): ExcelJS.Cell {
    const cell = worksheet.getCell(cellAddress);
    cell.value = value;

    this.applyCellStyle(cell, {
      font: { bold: true, size, color: { argb: ExcelColors.WHITE_TEXT } },
      fill: {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: bgColor },
      },
      alignment: { horizontal: 'center', vertical: 'middle' },
      border: {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      },
    });

    return cell;
  }

  /**
   * Create table header row
   */
  static createTableHeader(
    worksheet: ExcelJS.Worksheet,
    row: number,
    headers: string[],
    bgColor: string = ExcelColors.TABLE_HEADER
  ): void {
    headers.forEach((header, index) => {
      const cell = worksheet.getCell(row, index + 1);
      cell.value = header;

      this.applyCellStyle(cell, {
        font: { bold: true, color: { argb: ExcelColors.WHITE_TEXT } },
        fill: {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: bgColor },
        },
        alignment: { horizontal: 'center', vertical: 'middle' },
        border: {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        },
      });
    });
  }

  /**
   * Apply border to a cell
   */
  static applyBorder(cell: ExcelJS.Cell): void {
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  }

  /**
   * Format currency cell
   */
  static formatCurrencyCell(cell: ExcelJS.Cell, value: number): void {
    cell.value = value;
    cell.numFmt = '#,##0';
    this.applyBorder(cell);
  }

  /**
   * Format percentage cell
   */
  static formatPercentageCell(cell: ExcelJS.Cell, value: number): void {
    cell.value = value; // Don't divide by 100, keep original value
    cell.numFmt = '0.00"%"'; // Format as number with % symbol, not percentage format
    this.applyBorder(cell);
  }

  /**
   * Save Excel file
   */
  static async saveExcelFile(
    workbook: ExcelJS.Workbook,
    fileName: string
  ): Promise<void> {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, fileName);
  }

  /**
   * Generate file name with current date
   */
  static generateFileName(prefix: string, suffix?: string): string {
    const currentDate = new Date().toISOString().split('T')[0];
    return suffix
      ? `${prefix}_${suffix}_${currentDate}.xlsx`
      : `${prefix}_${currentDate}.xlsx`;
  }

  /**
   * Format Vietnamese date
   */
  static formatVietnameseDate(date: Date): string {
    return date.toLocaleDateString('vi-VN');
  }
}
